import { useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { useEffectOnce } from "react-use"

import { HUBSPOT_REQUIRED_PLANS } from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Badge,
	Button,
	HubspotIcon,
	IntegrationTemplate,
	Loader,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useSession } from "@/lib/session"
import { PlanPill } from "../plan-pill"
import { HubspotActiveFeatures } from "./hubspot-active-features"
import { getHubspotConnectFeedback } from "./hubspot-connect-feedback"
import { IntegrationLayout } from "./integration-layout"
import { IntegrationPlanBanner } from "./integration-plan-banner"

const HUBSPOT_TITLE = "Hubspot"
const HUBSPOT_DESCRIPTION =
	"See support history on every HubSpot record and create threads from HubSpot."
const HUBSPOT_PLAN_BANNER_DESCRIPTION =
	"Start a 7-day trial to embed Productlane in your Hubspot records and turn Hubspot properties into segments. The trial moves your workspace to Scale for those seven days."

const HUBSPOT_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How the Hubspot Integration Works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				The HubSpot integration embeds Productlane directly into your HubSpot
				workspace, allowing your sales and support teams to manage customer
				support requests without leaving HubSpot. A Productlane widget appears
				on every contact and company record, giving you instant access to
				support history and the ability to create new threads.
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Key Features
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Embedded Widget:</strong> Access
						Productlane from contact and company records.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Push Support Requests:</strong>{" "}
						Create threads directly from HubSpot.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Daily Support Notes:</strong> Each
						morning, every conversation from the past day lands as a note on the
						matching contact or company.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Contact and Company Push:</strong>{" "}
						Productlane contacts and companies are created in HubSpot as they
						appear.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">View History:</strong> See all
						support interactions for each contact.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Setup Instructions
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">1.</span>
					<span>
						Click &quot;Connect&quot; to authorize Productlane to access
						HubSpot.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">2.</span>
					<span>
						The Productlane widget will automatically appear in HubSpot.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">3.</span>
					<span>Start managing support requests from within HubSpot.</span>
				</div>
			</div>
		</div>

		<div className="mb-6">
			<div className="mb-2 text-base font-semibold text-primary">
				Perfect for Sales Teams
			</div>
			<div className="text-sm leading-relaxed text-secondary">
				This integration is ideal for teams who live in HubSpot. Sales reps can
				quickly check support status, create support threads for their
				customers, and see delivery status without switching between tools.
			</div>
		</div>
	</div>
)

export function HubspotIntegrationPage() {
	const workspaceQuery = api.workspace.info.useQuery()

	return (
		<>
			{workspaceQuery.data ? (
				<HubspotForm data={{ workspace: workspaceQuery.data }} />
			) : (
				<div className="flex justify-center">
					<Loader />
				</div>
			)}
		</>
	)
}

interface HubspotFormProps {
	data: {
		workspace: RouterOutputs["workspace"]["info"]
	}
}

function HubspotForm({ data }: HubspotFormProps) {
	const ctx = api.useUtils()
	const navigate = useNavigate()
	const search = useSearch({ strict: false })
	const feedback = getHubspotConnectFeedback(
		search.success ?? null,
		search.reason ?? null,
	)
	const [dialogOpen, setDialogOpen] = useState(false)
	const { session } = useSession()

	useEffectOnce(() => {
		if (!feedback) {
			return
		}
		if (feedback.kind === "connected") {
			toast.success({
				title: "Hubspot is now connected",
				icon: HubspotIcon,
			})
		} else if (feedback.kind === "plan-required") {
			toast.info({
				title: "Upgrade to Scale to connect Hubspot",
				description:
					"The Hubspot integration is included from the Scale plan up. Your workspace is on a lower plan, so the connection was not started.",
				icon: HubspotIcon,
				action: {
					label: "View plans",
					callback: () => {
						void navigate({ to: "/settings/plans" })
					},
				},
			})
		} else {
			toast.error({
				title: "Hubspot could not be connected",
				description:
					"The authorization did not finish. Start the connection again, or contact us if it keeps failing.",
				icon: HubspotIcon,
			})
		}
		void navigate({ to: "/settings/integrations/hubspot", replace: true })
	})
	const disconnectMutation = api.workspace.disconnectHubspot.useMutation({
		onSuccess: async () => {
			await ctx.workspace.info.invalidate()
		},
	})
	const { passes: hasRequiredPlan } = useRequiredPlans(HUBSPOT_REQUIRED_PLANS)
	const connected = data.workspace.hubspotConnected
	const reconnectRequired = data.workspace.hubspotReconnectRequired
	const [isLoading, setIsLoading] = useState(false)

	return (
		<>
			<MetaInfo title="Hubspot Integration" />
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<HubspotIcon className="w-10" />}
				name={
					<div className="inline-flex items-center gap-4">
						{HUBSPOT_TITLE}
						{connected ? (
							<Badge variant="green">Connected</Badge>
						) : (
							<PlanPill plan="scale" />
						)}
					</div>
				}
				description={HUBSPOT_DESCRIPTION}
				instructions={
					<>
						<IntegrationPlanBanner
							feature="Hubspot"
							requiredPlans={HUBSPOT_REQUIRED_PLANS}
							description={HUBSPOT_PLAN_BANNER_DESCRIPTION}
							icon={HubspotIcon}
						/>
						{reconnectRequired && (
							<div className="mt-4 flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700/80 dark:bg-gray-850">
								<div>
									<div className="text-sm font-medium text-primary">
										Reconnect HubSpot to log support notes
									</div>
									<div className="mt-0.5 text-sm text-secondary">
										Your connection predates the note permission, so daily
										support notes are paused until you reconnect.
									</div>
								</div>
								<Button
									Icon={HubspotIcon}
									onClick={() => {
										window.location.href = `${env.VITE_API_URL}/api/hubspot/connect`
									}}
								>
									Reconnect
								</Button>
							</div>
						)}
						{connected ? <HubspotActiveFeatures /> : HUBSPOT_LONG_DESCRIPTION}
					</>
				}
				button={
					hasRequiredPlan ? (
						connected ? (
							<Button
								isLoading={disconnectMutation.isLoading}
								onClick={() => {
									if (session?.membershipRole === "ADMIN") {
										setDialogOpen(true)
										return
									}
									toast.info({
										title: "Action not allowed",
										description:
											"Only admins have permission to disconnect integrations.",
										action: {
											label: "View your role",
											callback: () => {
												void navigate({ to: "/settings/members" })
											},
										},
									})
								}}
								disabled={session?.membershipRole !== "ADMIN"}
							>
								Disconnect
							</Button>
						) : (
							<RoleCheck not={["VIEWER"]}>
								<Button
									isLoading={isLoading}
									onClick={() => {
										setIsLoading(true)
										analytics.track("connect-hubspot")
										window.location.href = `${env.VITE_API_URL}/api/hubspot/connect`
									}}
								>
									Connect
								</Button>
							</RoleCheck>
						)
					) : (
						<UpgradeButton required={HUBSPOT_REQUIRED_PLANS} />
					)
				}
			/>

			<AlertDialog
				variant="positive"
				isOpen={dialogOpen}
				onOpenChange={setDialogOpen}
				confirmText="Disconnect"
				title="Disconnect Hubspot Integration?"
				description="Are you sure you want to disconnect Hubspot? Notes already in HubSpot stay there, new messages stop syncing, and your Productlane data remains saved."
				onCancel={() => {
					setDialogOpen(false)
				}}
				onConfirm={() => {
					analytics.track("disconnect-hubspot")
					disconnectMutation.mutate()
					setDialogOpen(false)
				}}
			/>
		</>
	)
}
